import React, { useEffect, useMemo, useRef, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Stack,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import DownloadIcon from "@mui/icons-material/Download";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import LastPageSharpIcon from "@mui/icons-material/LastPageSharp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
// Whitelist constant
const REFUNDED_WHITELIST = [
  145561, 146626, 148099, 148318, 175775, 182412, 148090, 148317, 186373,
  125036, 132908, 133323,
];

// ---- Helpers UI
const DashboardTile = ({ title, actions, children }) => (
  <Paper
    elevation={2}
    sx={{
      p: 2,
      borderRadius: 2,
      minHeight: 320,
      display: "flex",
      flexDirection: "column",
      gap: 1,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>{actions}</Box>
    </Box>
    <Divider />
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      {children}
    </Box>
  </Paper>
);

const StatusChip = ({ status }) => {
  const s = (status || "").toString().toLowerCase();
  let color = "default";
  if (
    s.includes("lipse") ||
    s.includes("lipsa") ||
    s === "lipsa awb" ||
    s === "lipsa curier"
  )
    color = "warning";
  else if (s === "delivered" || s.includes("livrat")) color = "success";
  else if (s === "exception" || s.includes("exception") || s.includes("return"))
    color = "error";
  else if (s === "out_for_delivery" || s.includes("livrare")) color = "info";
  else if (s === "in_transit") color = "primary";
  return <Chip size="small" label={status || "—"} color={color} />;
};

// ---- Data hooks
function useShipmentsAI(initialPageSize = 50) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [sinceDays, setSinceDays] = useState("7");
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [lastSeenId, setLastSeenId] = useState(0);
  const [live, setLive] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const syncWooOrders = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const rsp = await fetch(
        "https://server-9p4hx.kinsta.app/update-woocommerce-orders"
      );
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
    } catch (e) {
      console.error("syncWooOrders error", e);
    } finally {
      setSyncing(false);
    }
  };

  const esRef = useRef(null);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (onlyMissing) p.set("only_missing", "true");
    if (sinceDays && Number(sinceDays) > 0)
      p.set("since_days", String(sinceDays));
    p.set("page_size", String(pageSize));
    return p.toString();
  }, [onlyMissing, pageSize, sinceDays]);

  const fetchPage1 = async () => {
    setLoading(true);
    setError("");
    try {
      const base = queryString; // fără status
      const [rspLiv, rspRef, rspPay] = await Promise.all([
        fetch(
          `https://server-9p4hx.kinsta.app/api/ai/luxura-awb/awb-check-ai?status=livratclient&${base}`
        ),
        fetch(
          `https://server-9p4hx.kinsta.app/api/ai/luxura-awb/awb-check-ai?status=refunded&${base}`
        ),
        fetch(
          `https://server-9p4hx.kinsta.app/api/ai/luxura-awb/awb-check-ai?status=plataramburs&${base}`
        ),
      ]);
      if (!rspLiv.ok) throw new Error(`HTTP ${rspLiv.status} (livratclient)`);
      if (!rspRef.ok) throw new Error(`HTTP ${rspRef.status} (refunded)`);
      if (!rspPay.ok) throw new Error(`HTTP ${rspPay.status} (plataramburs)`);
      const [jsonLiv, jsonRef, jsonPay] = await Promise.all([
        rspLiv.json(),
        rspRef.json(),
        rspPay.json(),
      ]);

      const mapLiv = (jsonLiv.items || []).map((x) => ({
        ...x,
        isRefunded: false,
        isTransit: Boolean(x.isTransit),
      }));
      const mapRef = (jsonRef.items || [])
        .map((x) => ({
          ...x,
          isRefunded: true,
          isTransit: Boolean(x.isTransit),
        }))
        .filter((x) => !x.isTransit);
      const mapPay = (jsonPay.items || []).map((x) => ({
        ...x,
        isRefunded: false,
        isTransit: Boolean(x.isTransit),
        payOnDelivery: true,
      }));

      const combined = [
        ...mapLiv.filter((x) => x.isTransit),
        ...mapPay.filter((x) => x.isTransit),
        ...mapRef,
        ...mapLiv.filter((x) => !x.isTransit),
        ...mapPay.filter((x) => !x.isTransit),
      ];

      const filteredCombined = combined.filter(
        (x) => !(x.isRefunded && REFUNDED_WHITELIST.includes(Number(x.id)))
      );
      const seen = new Set();
      const deduped = filteredCombined.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
      setItems(deduped);
      const maxId = deduped.reduce((m, r) => Math.max(m, Number(r.id) || 0), 0);
      if (maxId > 0) setLastSeenId(maxId);
    } catch (e) {
      setError(e.message || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  // SSE live updates
  useEffect(() => {
    if (!live) return;
    const statuses = ["livratclient", "plataramburs"].join(",");
    const url = `https://server-9p4hx.kinsta.app/api/ai/luxura-awb/awb-stream?status=${encodeURIComponent(statuses)}&since_id=${lastSeenId}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener("new_order", (ev) => {
      try {
        const payload = JSON.parse(ev.data);
        setItems((prev) => {
          const next = [
            {
              id: payload.id,
              name: payload.name,
              courier: payload.courier,
              awb: payload.awb,
              status: payload.awb ? "—" : "LIPSA AWB",
              isRefunded: false,
            },
            ...prev,
          ];
          return next.slice(0, 300);
        });
        const evId = Number(ev.lastEventId || payload.id || 0);
        if (evId > 0) setLastSeenId((m) => Math.max(m, evId));
      } catch {}
    });
    es.addEventListener("error", () => {
      /* reconectare automată implicită */
    });

    return () => {
      es.close();
    };
  }, [live, lastSeenId]);

  return {
    state: {
      items,
      loading,
      error,
      onlyMissing,
      sinceDays,
      pageSize,
      live,
      syncing,
    },
    setOnlyMissing,
    setSinceDays,
    setPageSize,
    setLive,
    refresh: fetchPage1,
    syncWooOrders,
  };
}

// ---- Formatting helpers
const formatCourier = (courier) => {
  if (!courier) return "—";
  const s = courier.toString().toLowerCase();
  if (s.includes("gls")) return "GLS";
  if (s.includes("fan")) return "Fancurier";
  if (s.includes("dpd")) return "DPD";
  if (s.includes("sameday")) return "Sameday";
  return courier;
};

// ---- Tiles
function ShipmentsTrackerTile() {
  const { state, setLive, refresh, syncWooOrders } = useShipmentsAI(50);
  const { items, loading, error, live } = state;

  useEffect(() => {
    refresh();
  }, []);

  return (
    <DashboardTile
      title="Urmărire colete"
      actions={
        <Stack direction="row" spacing={1}>
          <Tooltip title="Reîmprospătează acum">
            <span>
              <IconButton onClick={refresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              state.syncing
                ? "Sincronizare în curs..."
                : "Actualizează comenzile WooCommerce"
            }
          >
            <span>
              <IconButton
                onClick={syncWooOrders}
                disabled={state.syncing || loading}
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={live ? "Live ON (SSE)" : "Live OFF"}>
            <IconButton onClick={() => setLive(!live)}>
              {live ? <WifiIcon /> : <WifiOffIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      }
    >
      <Box sx={{ position: "relative", flex: 1, overflow: "auto" }}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 2 }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}

        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={80}>Tip</TableCell>
              <TableCell width={90}>ID</TableCell>
              <TableCell>Nume</TableCell>
              <TableCell width={140}>Curier</TableCell>
              <TableCell width={160}>Status AWB</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  {r.isRefunded ? (
                    <Stack
                      direction="column"
                      spacing={0}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <KeyboardReturnIcon fontSize="small" />
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.65rem", lineHeight: 1 }}
                      >
                        RETUR
                      </Typography>
                    </Stack>
                  ) : r.isTransit ? (
                    <Stack
                      direction="column"
                      spacing={0}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <LastPageSharpIcon fontSize="small" />
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.65rem", lineHeight: 1 }}
                      >
                        CERERE
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack
                      direction="column"
                      spacing={0}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <AssignmentTurnedInIcon fontSize="small" />
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.65rem", lineHeight: 1 }}
                      >
                        COMANDA
                      </Typography>
                    </Stack>
                  )}
                </TableCell>
                <TableCell>{r.id}</TableCell>
                <TableCell>{r.name || "—"}</TableCell>
                <TableCell>{formatCourier(r.courier)}</TableCell>
                <TableCell>
                  <StatusChip status={r.status} />
                </TableCell>
              </TableRow>
            ))}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography
                    variant="body2"
                    sx={{ py: 3, textAlign: "center", color: "text.secondary" }}
                  >
                    Niciun rezultat pentru filtrele actuale.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </DashboardTile>
  );
}

// --- Analiza
const severityColor = (sev) => {
  switch ((sev || "").toLowerCase()) {
    case "high":
      return "#f44336";
    case "medium":
      return "#ff9800";
    case "low":
      return "#2196f3";
    default:
      return "#9e9e9e";
  }
};
const severityIcon = (sev) => {
  const s = (sev || "").toLowerCase();
  if (s === "high") return <ErrorOutlineIcon fontSize="small" />;
  if (s === "medium") return <WarningAmberIcon fontSize="small" />;
  return <InfoOutlinedIcon fontSize="small" />;
};

function WarningItem({ issue }) {
  const { id, message, severity = "low", detected_at, actions = [] } = issue;

  // Add animated feedback for "Repara LIA" button
  const [repairingStep, setRepairingStep] = useState(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Steps for feedback
  const repairSteps = [
    { label: "Analizează...", delay: 700 },
    { label: "Repară...", delay: 1000 },
    { label: "Finalizează...", delay: 900 },
  ];

  // Handler for "Repara LIA"
  const handleRepair = async () => {
    setRepairingStep(0);
    // Animate through steps
    for (let i = 0; i < repairSteps.length; ++i) {
      setRepairingStep(i);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, repairSteps[i].delay));
    }
    // Actually call the repair endpoint at the start of the repair
    try {
      const rsp = await fetch(
        `https://server-9p4hx.kinsta.app/api/ai/luxura-awb/awb-repair/${id}`,
        { method: "POST" }
      );
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
      setSnackbarMsg("Reparat cu succes!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      console.log("Repara LIA: Success for", id);
    } catch (e) {
      setSnackbarMsg("Eroare la reparare: " + (e.message || ""));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Repara LIA: Error for", id, e);
    }
    // Reset after a short pause
    setTimeout(() => setRepairingStep(null), 800);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const showRepairButton =
    typeof message === "string" &&
    message.includes("Nu este creat storno în JIRA");

  // Handlers for icon buttons
  const handleOpenOrder = () => {
    window.open(
      `https://luxuraelite.ro/wp-admin/post.php?post=${id}&action=edit`,
      "_blank"
    );
  };
  const handleOpenJira = () => {
    const jiraKey = issue.jiraKey || id;
    window.open(`https://luxura.atlassian.net/browse/${jiraKey}`, "_blank");
  };

  return (
    <Box
      sx={{
        borderLeft: `4px solid ${severityColor(severity)}`,
        backgroundColor: "background.paper",
        borderRadius: 1,
        p: 1.5,
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
      }}
    >
      <Box sx={{ mt: "2px" }}>{severityIcon(severity)}</Box>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "row",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              cursor: actions[0]?.href ? "pointer" : "default",
              color: actions[0]?.href ? "primary.main" : "text.primary",
              wordBreak: "break-word",
            }}
            onClick={() =>
              actions[0]?.href && window.open(actions[0].href, "_blank")
            }
          >
            {message}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {`Comanda #${id} • `}
            {issue.created_at
              ? new Date(issue.created_at).toLocaleString()
              : detected_at
                ? new Date(detected_at).toLocaleString()
                : "detectat"}
          </Typography>
        </Box>
        {showRepairButton && (
          <Box
            sx={{
              ml: "auto",
              pl: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.5,
            }}
          >
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={handleRepair}
              disabled={repairingStep !== null}
              startIcon={
                repairingStep !== null ? (
                  <CircularProgress color="inherit" size={16} />
                ) : undefined
              }
              sx={{ mb: 0.5 }}
            >
              {repairingStep !== null
                ? repairSteps[repairingStep]?.label || "Finalizează..."
                : "Repara LIA"}
            </Button>
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              <Tooltip title="Deschide Order">
                <IconButton size="small" onClick={handleOpenOrder}>
                  <AssignmentTurnedInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Deschide JIRA">
                <IconButton size="small" onClick={handleOpenJira}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

function AnalysisTile() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchIssues = async () => {
    setLoading(true);
    setError("");
    try {
      let rsp = await fetch(
        "https://server-9p4hx.kinsta.app/api/ai/luxura-awb/awb-issues"
      );
      if (rsp.status === 404) {
        rsp = await fetch(
          "https://server-9p4hx.kinsta.app/api/ai/luxura-awb/awb-check-ai?only_missing=true&page_size=100"
        );
      }
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
      const json = await rsp.json();

      const raw = json.issues || json.items || [];
      const mapped = raw.map((r) => {
        if (r.message) return r;
        const msg = !r.courier
          ? `Comanda ${r.id} lipsa CURIER – te rog setează curierul`
          : `Comanda ${r.id} lipsa AWB – te rog scrie nr. de AWB în comandă imediat`;
        const sev = !r.courier ? "high" : "medium";
        return {
          id: r.id,
          message: msg,
          severity: sev,
          detected_at: Date.now(),
        };
      });
      const filtered = mapped.filter(
        (r) => !REFUNDED_WHITELIST.includes(Number(r.id))
      );
      setIssues(filtered);
    } catch (e) {
      setError(e.message || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, []);

  return (
    <DashboardTile
      title="Analiza Comenzi"
      actions={
        <Tooltip title="Reîmprospătează analizele">
          <span>
            <IconButton onClick={fetchIssues} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      <Box
        sx={{
          position: "relative",
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Box sx={{ p: 2 }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}
        {!loading && !error && issues.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Nicio problemă detectată în acest moment.
            </Typography>
          </Box>
        )}
        {!loading &&
          !error &&
          issues.map((issue, idx) => (
            <WarningItem key={`${issue.id}-${idx}`} issue={issue} />
          ))}
      </Box>
    </DashboardTile>
  );
}

export default function LuxuraAI_AWB() {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <ShipmentsTrackerTile />
      </Grid>
      <Grid item xs={12} md={6}>
        <AnalysisTile />
      </Grid>
    </Grid>
  );
}
